import { createPortal } from "react-dom";
import { CseMessageBanner, CseMessageModal, CseMessageToaster } from "yeati-ui";
import { useCseMessages } from "@/features/cse-messages/hooks/useCseMessages";
import { useDismissCseMessage } from "@/features/cse-messages/hooks/useDismissCseMessage";
import type { CseMessage } from "@/features/cse-messages/types/cseMessage.type";

const toWidgetProps = (message: CseMessage) => ({
  title: message.title,
  bodyHtml: message.body ?? undefined,
  ctaLabel: message.cta_label ?? undefined,
  ctaUrl: message.cta_url ?? undefined,
  backgroundColor: message.background_color ?? undefined,
  textColor: message.text_color ?? undefined,
});

export const CseMessages = () => {
  const { modalQueue, banners, toasters } = useCseMessages();
  const { mutate: dismiss } = useDismissCseMessage();

  // La file avance via le retrait optimiste du cache : au dismiss, la modale
  // suivante devient modalQueue[0]. key={id} rejoue l'animation d'entrée.
  const currentModal = modalQueue[0];

  return (
    <>
      {banners.length > 0 && (
        <div className="mb-4 flex flex-col gap-2 pt-4">
          {banners.map((message) => (
            <CseMessageBanner
              key={message.id}
              {...toWidgetProps(message)}
              onDismiss={() => dismiss(message.id)}
            />
          ))}
        </div>
      )}

      {/* Portals : la sidebar est un élément fixed z-40 frère du wrapper
          `isolate` du contenu — un z-index interne ne peut pas passer devant.
          On rend donc modale et toasters à la racine du document. */}
      {currentModal && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="cse-message-modal-overlay"
        >
          <CseMessageModal
            key={currentModal.id}
            {...toWidgetProps(currentModal)}
            coverImageUrl={currentModal.cover_image ?? undefined}
            onDismiss={() => dismiss(currentModal.id)}
          />
        </div>,
        document.body,
      )}

      {toasters.length > 0 && createPortal(
        <div className="fixed bottom-24 right-4 z-40 flex max-h-[60vh] w-full max-w-sm flex-col-reverse gap-2 overflow-y-auto">
          {toasters.map((message) => (
            <CseMessageToaster
              key={message.id}
              {...toWidgetProps(message)}
              onDismiss={() => dismiss(message.id)}
            />
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};
